import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appInfiniteScroll]',
  standalone: true,
})
export class InfiniteScrollDirective implements AfterViewInit, OnDestroy {
  private el = inject(ElementRef<HTMLElement>);
  private ngZone = inject(NgZone);

  readonly distance = input<string>('10%');
  readonly fromTop = input<boolean>(false);
  readonly debounceTime = input<number>(200);
  readonly disabled = input<boolean>(false);

  readonly scrolled = output<void>();

  private destroyed$ = new Subject<void>();

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(this.el.nativeElement, 'scroll')
        .pipe(debounceTime(this.debounceTime()), takeUntil(this.destroyed$))
        .subscribe((event) => this.handleScroll(event as Event));
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private handleScroll(event: Event) {
    if (this.disabled()) return;

    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    let thresholdPx = 0;

    if (this.distance().includes('%')) {
      thresholdPx = (parseFloat(this.distance()) / 100) * scrollHeight;
    } else {
      thresholdPx = parseFloat(this.distance());
    }

    if (this.fromTop()) {
      if (scrollTop <= thresholdPx) {
        this.ngZone.run(() => this.scrolled.emit());
      }
    } else {
      const remaining = scrollHeight - (scrollTop + clientHeight);
      if (remaining <= thresholdPx) {
        this.ngZone.run(() => this.scrolled.emit());
      }
    }
  }
}
